import React, { useEffect, useRef, useState } from 'react';
import api from '../../apiClient';
import './CampaignTracker.css';

// Poll while the campaign is still working through its recipients.
const ACTIVE = new Set(['approved', 'sending', 'queued', 'pending']);
// Terminal = done, one way or another. A terminal campaign is never "live",
// even if a straggler recipient is still marked queued days later.
const TERMINAL = new Set(['completed', 'failed', 'cancelled', 'partial']);

// A send that ended failed/cancelled but actually reached people is partial,
// not failed — 197 of 303 sent should not read as a total loss.
const displayStatus = (c) => {
  const st = (c?.status || '').toLowerCase();
  if ((st === 'failed' || st === 'cancelled') && (c?.sent || 0) > 0) return 'partial';
  if (!ACTIVE.has(st) && (c?.failed || 0) > 0 && (c?.sent || 0) > 0) return 'partial';
  return st || 'unknown';
};

export default function CampaignTracker({ campaignId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const timer = useRef(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get(`/customers/outreach/campaigns/${campaignId}/`);
        if (!alive) return;
        setData(r.data);
        setError('');
        const st = (r.data?.campaign?.status || '').toLowerCase();
        const queued = r.data?.campaign?.queued || 0;
        // Poll only while the campaign is still working (stop on terminal),
        // and at 15s — a per-5s poll held a DB connection each cycle and drove
        // the connection climb. 15s is plenty for a live send view. A terminal
        // campaign never re-polls, even with a stuck-queued straggler.
        if (!TERMINAL.has(st) && (ACTIVE.has(st) || queued > 0)) timer.current = setTimeout(load, 15000);
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.data?.error || 'Failed to load campaign');
        timer.current = setTimeout(load, 15000);
      }
    };
    load();
    return () => { alive = false; if (timer.current) clearTimeout(timer.current); };
  }, [campaignId]);

  const c = data?.campaign;
  const recips = data?.recipients || [];
  const pct = c && c.total ? Math.round((c.sent / c.total) * 100) : 0;
  const shownStatus = c ? displayStatus(c) : 'unknown';
  // Live only while genuinely still working — never for a terminal campaign,
  // no matter how many recipients are stuck queued.
  const live = !!c && !TERMINAL.has((c.status || '').toLowerCase())
    && !TERMINAL.has(shownStatus)
    && (ACTIVE.has((c.status || '').toLowerCase()) || (c.queued || 0) > 0);

  const stats = c ? [
    { k: 'Total', v: c.total, cls: '' },
    { k: 'Sent', v: c.sent, cls: 'ok' },
    { k: 'Delivered', v: c.delivered, cls: 'ok' },
    { k: 'Replied', v: c.replied, cls: 'reply' },
    { k: 'Opted out', v: c.opted_out, cls: 'neg' },
    { k: 'Failed', v: c.failed, cls: 'neg' },
    { k: 'Follow-ups', v: c.scheduled_followups, cls: 'warn' },
    { k: 'Queued', v: c.queued, cls: 'warn' },
  ] : [];
  // Email only: Mailgun's own events (comm_app/engagement.py), never inferred.
  const eng = c?.engagement;
  const engStats = eng ? [
    { k: 'Delivered (Mailgun)', v: eng.delivered, cls: 'ok' },
    { k: 'Opened', v: eng.opened, cls: 'reply' },
    { k: 'Clicked', v: eng.clicked, cls: 'reply' },
    { k: 'Bounced', v: eng.bounced, cls: 'neg' },
    { k: 'Complained', v: eng.complained, cls: 'neg' },
    { k: 'Unsubscribed', v: eng.unsubscribed, cls: 'neg' },
  ] : [];

  return (
    <div className="cxt-overlay" role="dialog" aria-modal="true">
      <div className="cxt-panel">
        <div className="cxt-head">
          <div>
            <div className="cxt-title">{c?.name || 'Campaign'}</div>
            <div className="cxt-sub">
              <span className={`cxt-badge cxt-${shownStatus}`}>
                {shownStatus}
              </span>
              {live && <span className="cxt-live">● live</span>}
            </div>
          </div>
          <button className="cxt-x" onClick={onClose} aria-label="Close tracker">✕</button>
        </div>

        {error && <div className="cxt-error">{error}</div>}

        <div className="cxt-bar"><div className="cxt-bar-fill" style={{ width: `${pct}%` }} /></div>
        <div className="cxt-pct">{pct}% dispatched{live ? ' — refreshing every 15s' : ''}</div>

        <div className="cxt-stats">
          {stats.map((s) => (
            <div key={s.k} className={`cxt-stat cxt-${s.cls}`}>
              <div className="cxt-stat-v">{s.v ?? 0}</div>
              <div className="cxt-stat-k">{s.k}</div>
            </div>
          ))}
        </div>
        {eng ? (
          <>
            <div className="cxt-pct" title={eng.line}>Engagement · {eng.line}</div>
            <div className="cxt-stats">
              {engStats.map((s) => (
                <div key={s.k} className={`cxt-stat cxt-${s.cls}`}>
                  <div className="cxt-stat-v">{s.v ?? 0}</div>
                  <div className="cxt-stat-k">{s.k}</div>
                </div>
              ))}
            </div>
          </>
        ) : null}

        <div className="cxt-table-wrap">
          <table className="cxt-table">
            <thead>
              <tr><th>Recipient</th><th>Phone</th><th>Status</th><th>Reply</th></tr>
            </thead>
            <tbody>
              {recips.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td className="cxt-mono">{r.phone}</td>
                  <td>
                    <span className={`cxt-rstatus cxt-r-${r.opted_out ? 'optout' : r.replied ? 'replied' : r.status}`}>
                      {r.opted_out ? 'opted out' : r.replied ? 'replied' : r.status}
                    </span>
                  </td>
                  <td className="cxt-reply">{r.response || (r.status === 'failed' ? r.error : '')}</td>
                </tr>
              ))}
              {recips.length === 0 && (
                <tr><td colSpan={4} className="cxt-empty">No recipients yet…</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
